import { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  Eye,
  Heart,
  MessageSquare,
  Share2,
  Calendar,
  Download,
  BarChart3,
  FileText
} from 'lucide-react';
import { adminAnalyticsApi } from '../utils/apiService';
import { toast } from 'sonner';

interface TopThought {
  id: string;
  title: string;
  likes: number;
  comments: number;
  shares: number;
  views: number;
  engagement: number;
}

export default function Analytics() {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'year'>('30d');
  const [isLoading, setIsLoading] = useState(false);

  // API data state
  const [stats, setStats] = useState({
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    totalShares: 0,
    avgEngagement: 0,
    newUsers: 0,
    activeUsers: 0,
    totalUsers: 0,
    totalThoughts: 0,
  });

  const [topThoughts, setTopThoughts] = useState<TopThought[]>([]);

  // Fetch analytics data from API
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        const result = await adminAnalyticsApi.get();
        const data = result.analytics || result;

        const totalLikes = data.topThoughts?.reduce((sum: number, t: any) => sum + (t.stats?.likes || 0), 0) || 0;
        const totalShares = data.topThoughts?.reduce((sum: number, t: any) => sum + (t.stats?.shares || 0), 0) || 0;
        const totalViews = data.topThoughts?.reduce((sum: number, t: any) => sum + (t.stats?.views || 0), 0) || 0;

        setStats({
          totalViews,
          totalLikes,
          totalComments: data.totalComments || 0,
          totalShares,
          avgEngagement: data.avgEngagement || 0,
          newUsers: data.newUsers || 0,
          activeUsers: data.activeUsers || 0,
          totalUsers: data.totalUsers || 0,
          totalThoughts: data.totalThoughts || 0,
        });

        const mapped: TopThought[] = (data.topThoughts || []).map((t: any, i: number) => {
          const likes = t.stats?.likes || 0;
          const comments = t.stats?.comments || 0;
          const shares = t.stats?.shares || 0;
          const views = t.stats?.views || 0;
          const total = likes + comments + shares + views;
          const engagement = total > 0 ? Math.round(((likes + comments + shares) / total) * 100) : 0;
          return {
            id: t._id || String(i),
            title: t.text ? (t.text.length > 60 ? t.text.slice(0, 60) + '…' : t.text) : 'Unknown',
            likes,
            comments,
            shares,
            views,
            engagement,
          };
        });
        setTopThoughts(mapped);

      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  const handleExport = () => {
    const csvRows = [
      ['Metric', 'Value'],
      ['Total Views', stats.totalViews],
      ['Total Likes', stats.totalLikes],
      ['Total Comments', stats.totalComments],
      ['Total Shares', stats.totalShares],
      ['Active Users', stats.activeUsers],
      ['Total Users', stats.totalUsers],
      ['Total Thoughts', stats.totalThoughts],
    ];
    const csvContent = csvRows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Analytics Dashboard</h2>
          <p className="text-slate-600">Track engagement, growth, and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          {isLoading && <span className="text-sm text-slate-500">Loading...</span>}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#236266] transition-colors"
          >
            <Download className="w-5 h-5" />
            Export Report
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-slate-600" />
          <span className="text-sm font-semibold text-slate-700 mr-4">Time Period:</span>
          <div className="flex gap-2">
            {['7d', '30d', '90d', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === range
                    ? 'bg-[#2B7A7E] text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {range === '7d' ? 'Last 7 Days' :
                 range === '30d' ? 'Last 30 Days' :
                 range === '90d' ? 'Last 90 Days' :
                 'This Year'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="mb-4">
            <div className="p-3 bg-blue-100 rounded-xl inline-block">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-800">{stats.totalViews.toLocaleString()}</p>
          <p className="text-sm text-slate-600 mt-1">Total Views</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="mb-4">
            <div className="p-3 bg-red-100 rounded-xl inline-block">
              <Heart className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-800">{stats.totalLikes.toLocaleString()}</p>
          <p className="text-sm text-slate-600 mt-1">Total Likes</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="mb-4">
            <div className="p-3 bg-green-100 rounded-xl inline-block">
              <MessageSquare className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-800">{stats.totalComments.toLocaleString()}</p>
          <p className="text-sm text-slate-600 mt-1">Total Comments</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="mb-4">
            <div className="p-3 bg-purple-100 rounded-xl inline-block">
              <Share2 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-800">{stats.totalShares.toLocaleString()}</p>
          <p className="text-sm text-slate-600 mt-1">Total Shares</p>
        </div>
      </div>

      {/* Platform Summary */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-xl font-bold text-slate-800 mb-6">Platform Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[#2B7A7E]">{stats.totalUsers.toLocaleString()}</p>
            <p className="text-sm text-slate-600 mt-1">Total Users</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[#2B7A7E]">{stats.totalThoughts.toLocaleString()}</p>
            <p className="text-sm text-slate-600 mt-1">Total Thoughts</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[#2B7A7E]">{stats.activeUsers.toLocaleString()}</p>
            <p className="text-sm text-slate-600 mt-1">Active Users (7d)</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[#2B7A7E]">{stats.totalComments.toLocaleString()}</p>
            <p className="text-sm text-slate-600 mt-1">Total Comments</p>
          </div>
        </div>
      </div>

      {/* User Growth & Engagement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Metrics */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-6">User Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Active Users</p>
                  <p className="text-xl font-bold text-slate-800">{stats.activeUsers.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">New Users</p>
                  <p className="text-xl font-bold text-slate-800">{stats.newUsers}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Users</p>
                  <p className="text-xl font-bold text-slate-800">{stats.totalUsers.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Heart className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Avg. Engagement</p>
                  <p className="text-xl font-bold text-slate-800">{stats.avgEngagement}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Thoughts Engagement */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-6">Top Thoughts by Likes</h3>
          <div className="space-y-4">
            {topThoughts.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No data available</p>
            ) : (
              topThoughts.map((thought, index) => {
                const maxLikes = Math.max(...topThoughts.map(t => t.likes), 1);
                return (
                  <div key={thought.id} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700 truncate max-w-[60%]">{thought.title}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-600">{thought.likes} likes</span>
                        <span className="font-semibold text-[#2B7A7E]">#{index + 1}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#2B7A7E] to-[#D4AF37] rounded-full transition-all"
                        style={{ width: `${(thought.likes / maxLikes) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Top Performing Thoughts */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-800">Top Performing Thoughts</h3>
          <p className="text-sm text-slate-600 mt-1">Based on overall engagement</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Rank</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Thought</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700">Views</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700">Likes</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700">Comments</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700">Shares</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {topThoughts.map((thought, index) => (
                <tr key={thought.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-[#D4AF37] text-white' :
                      index === 1 ? 'bg-slate-300 text-white' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <span className="font-medium text-slate-800">{thought.title}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-700">
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span>{thought.views.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-700">
                      <Heart className="w-4 h-4 text-red-400" />
                      <span>{thought.likes}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-700">
                      <MessageSquare className="w-4 h-4 text-blue-400" />
                      <span>{thought.comments}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-700">
                      <Share2 className="w-4 h-4 text-green-400" />
                      <span>{thought.shares}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#e2e8f0"
                            strokeWidth="4"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#2B7A7E"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${thought.engagement * 1.76} 176`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-slate-800">{thought.engagement}%</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Note */}
      <div className="bg-gradient-to-r from-[#2B7A7E] to-[#236266] rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-lg font-bold mb-2">Overall Performance</h4>
            <p className="text-white/90 text-sm leading-relaxed">
              Platform has {stats.totalUsers.toLocaleString()} users with {stats.activeUsers.toLocaleString()} active in the last 7 days.
              {stats.totalThoughts > 0 && ` ${stats.totalThoughts.toLocaleString()} thoughts published with ${stats.totalComments.toLocaleString()} total comments.`}
              {topThoughts.length > 0 && ` Top thought: "${topThoughts[0].title}" with ${topThoughts[0].likes} likes.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
